import { Form } from "@skv/core";
import classNames from "classnames";
import { useRef } from "react";
import { Field } from "react-yup";

export interface PasteInputProps {
  id: string;
  value?: string;
  touched?: boolean;
  error?: string;
  field: Field;
  label: React.ReactFragment | string;
  helpText?: React.ReactFragment | string;
  onPaste: (fromClipboard: string) => void;
  className?: string;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  helpTextProps?: React.HTMLAttributes<HTMLElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  inputWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  customValidationError?: React.ReactNode;
}

export const PasteInput = (props: PasteInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePasteEvent = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");

    if (text) {
      props.onPaste(text);
    }
  };

  const handlePasteButtonClick = async () => {
    const canReadClipboard =
      typeof window !== "undefined" &&
      window.isSecureContext &&
      !!navigator.clipboard &&
      typeof navigator.clipboard.readText === "function";

    if (canReadClipboard) {
      try {
        const text = await navigator.clipboard.readText();

        if (text) {
          props.onPaste(text);
          return;
        }
      } catch (error) {
        // Firefox or browser policy may block programmatic clipboard reads.
      }
    }

    // Fallback: focus the input so the user can press Ctrl+V / Cmd+V.
    inputRef.current?.focus();
  };

  return (
    <div className={props.className}>
      <label htmlFor={props.id} {...props.labelProps}>
        {props.label}
      </label>

      {props.helpText && (
        <small id={`${props.id}-help-text`} {...props.helpTextProps}>
          {props.helpText}
        </small>
      )}

      <div {...props.inputWrapperProps}>
        <input
          {...props.field}
          {...props.inputProps}
          ref={inputRef}
          onPaste={handlePasteEvent}
          id={props.id}
          name={props.id}
          className={classNames(props.inputProps?.className, {
            "is-invalid": !!props.error && props.touched,
          })}
          value={props.value || ""}
          aria-invalid={!!props.error && props.touched}
          aria-describedby={
            props.error && props.touched
              ? `${props.id}-error-message ${props.id}-help-text`
              : `${props.id}-help-text`
          }
        />
        <div className="input-group-append">
          <button
            type="button"
            onClick={handlePasteButtonClick}
            {...props.buttonProps}
          >
            Paste
          </button>
        </div>
      </div>
      {props.touched &&
        props.error &&
        (props.customValidationError ?? (
          <Form.ValidationError
            errorId={`${props.id}-error-message`}
            message={props.error}
            className="mb-3"
          />
        ))}
    </div>
  );
};
